import { LazyExoticComponent, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { FormPageStepProps } from '@features/campaigns/builder/containers/FormPages/FormPages.types';
import { useTransformCampaignDetailsToStepData } from '@features/campaigns/builder/hooks/useTransformCampaignDetailsToStepData.ts';
import { Spinner } from '@features/campaigns/shared/components/Spinner/Spinner';
import useCampaignDetails from '@features/campaigns/shared/hooks/useCampaignDetails';
import { CampaignCountryProvider } from '@features/campaigns/shared/providers/CampaignCountryProvider/CampaignCountryProvider.tsx';
import { PossibleStepNumber } from '@features/campaigns/shared/types/campaign.ts';
import { AccessRights } from '@shared/api/commonApiInterfaces.ts';
import { PlaceholderCard } from '@shared/components/PlaceholderCard/PlaceholderCard.tsx';
import { i18nNameSpace } from '@shared/consts/i18n';
import { routePaths } from '@shared/router/routePaths';
import { logError } from '@shared/utils/logError.ts';
import { navigateTo } from '@shared/utils/navigateTo.tsx';

export function withCampaignSavedData<T>(Component: LazyExoticComponent<(props: FormPageStepProps<T>) => JSX.Element>) {
  return function EnhancedComponent({ campaignStep }: { campaignStep: PossibleStepNumber }) {
    const { t } = useTranslation(i18nNameSpace.API);

    const { campaignId } = useParams();
    const { isFetching, data } = useCampaignDetails({
      campaignId: campaignId!,
      campaignStep,
    });
    const navigate = useNavigate();

    const stepSavedData = useTransformCampaignDetailsToStepData(data, campaignStep);

    if (!data) {
      return <PlaceholderCard textKey='placeholderLoading' />;
    }

    const canEditCampaign =
      data.accessRights.includes(AccessRights.EDIT_ALL) ||
      data.accessRights.includes(AccessRights.EDIT_BUDGET) ||
      data.accessRights.includes(AccessRights.EDIT_END_DATE) ||
      data.accessRights.includes(AccessRights.EDIT_TARGETING) ||
      data.accessRights.includes(AccessRights.EDIT_BRAND) ||
      data.accessRights.includes(AccessRights.EDIT_ADDITIONAL_NOTE) ||
      data.accessRights.includes(AccessRights.EDIT_OVERRIDE_CPM) ||
      data.accessRights.includes(AccessRights.EDIT_OVERRIDE_IMPRESSIONS) ||
      data.accessRights.includes(AccessRights.EDIT_BONUS_IMPRESSIONS) ||
      data.accessRights.includes(AccessRights.EDIT_BONUS_PRICE) ||
      data.accessRights.includes(AccessRights.EDIT_INVENTORY);

    if (!canEditCampaign && data.accessRights.includes(AccessRights.READ)) {
      void navigateTo(navigate, routePaths.campaignDetails.url(campaignId || ''), {
        warningMessage: t('api.warning.campaign.edit.not_allowed'),
      }).catch(logError);
    }

    if (!data.accessRights.includes(AccessRights.READ)) {
      void navigateTo(navigate, routePaths.campaigns, {
        warningMessage: t('api.warning.campaign.read.not_allowed'),
      }).catch(logError);
    }

    return (
      <Suspense fallback={<PlaceholderCard textKey={'placeholderLoading'} />}>
        {isFetching && <Spinner variant='fullScreenOverlay' />}
        {/* // TODO - remove assertion stepSavedData as T in https://clearcodehq.atlassian.net/browse/PBLTL-648 */}
        <CampaignCountryProvider countryCode={data.country}>
          <Component
            campaignId={campaignId as string}
            campaignType={data.type}
            mediaType={data.mediaType}
            stepSavedData={stepSavedData as T}
            accessRights={data.accessRights}
          />
        </CampaignCountryProvider>
      </Suspense>
    );
  };
}
